import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Fine, FineNoCarDependency, FineNotification, FineVersion, NotifyFine, UpsertFine } from "./types";

export const useAdminFinesApi = () => {
  const api = useApi();

  const getAdminFine = useCallback(
    (fineId: string, signal: AbortSignal) => {
      const url = `/admin/fines/${fineId}`;
      return useSWRMutation<Fine | null, CustomError, string>(url, (url) => api.get<Fine>({ url, signal }));
    },
    [api],
  );

  const useAdminFines = useCallback(
    <K extends keyof FineNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<FineNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<FineNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/fines` : "/admin/fines",
        query,
      );
      return useSWR<Paginated<NestedPick<FineNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<FineNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminFine = useCallback(
    async (body: UpsertFine): Promise<{ id: string }> => {
      const url = `/admin/fines`;
      const id = await api.post<UpsertFine, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}/fines`]);
      return id;
    },
    [api],
  );

  const updateAdminFine = useCallback(
    async (id: string, body: UpsertFine) => {
      const url = `/admin/fines/${id}`;
      await api.put<UpsertFine>(url, body);
      api.mutateCache([`/admin/fines`, `/admin/cars/${body.carId}/fines`, `/admin/fines/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminFine = useCallback(
    async (fineId: string, carId: string): Promise<void> => {
      const url = `/admin/fines/${fineId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/fines`, `/admin/cars/${carId}/fines`]);
      });
    },
    [api],
  );

  const notifyDriver = useCallback(
    async (fineId: string, body: NotifyFine): Promise<void> => {
      const url = `/admin/fines/${fineId}/notify`;
      await api.put<NotifyFine>(url, body);
      api.mutateCache([
        `/admin/fines/${fineId}`,
        `/admin/fines`,
        `/admin/fines/${fineId}/notifications`,
        `/admin/fines/${fineId}/versions`,
      ]);
    },
    [api],
  );

  const archiveFine = useCallback(
    async (fineId: string): Promise<void> => {
      const url = `/admin/fines/${fineId}/archive`;
      await api.put<undefined>(url, undefined);
      api.mutateCache([`/admin/fines/${fineId}`, `/admin/fines`]);
    },
    [api],
  );

  const useFineNotifications = useCallback(
    (query: PaginationQuery, signal: AbortSignal, fineId: string) => {
      const url = buildUrl(`/admin/fines/${fineId}/notifications`, query);
      return useSWR<Paginated<FineNotification> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<FineNotification>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminFineHistory = useCallback(
    (query: PaginationQuery, fineID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/fines/${fineID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminFineVersion = useCallback(
    (fineId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/fines/${fineId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<FineVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<FineVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/fines/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminFine,
    updateAdminFine,
    insertAdminFine,
    useAdminFines,
    getAdminFine,
    notifyDriver,
    archiveFine,
    useFineNotifications,
    getAdminFineHistory,
    getAdminFineVersion,
    getFieldFilters,
  };
};
